import React from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { DesignDialog } from "../../../../common/dashboard/DesignDialog";

// CMP Dashboard (24) credPop: 460px card, 72px gold key circle, uppercase
// 16/800 title, Cancel + gold Download (design buttons, 8px radius).
const cancelButtonStyle: React.CSSProperties = {
  border: "1px solid #0A0A0A",
  background: "#fff",
  color: "#0A0A0A",
  borderRadius: 8,
  padding: "11px 22px",
  fontSize: 14,
  fontWeight: 600,
  maxWidth: "none",
};

const downloadButtonStyle: React.CSSProperties = {
  border: "none",
  color: "#fff",
  borderRadius: 8,
  padding: "11px 22px",
  fontSize: 14,
  fontWeight: 600,
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  maxWidth: "none",
};

const KeyGlyph = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="#fff"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: "50%", height: "50%" }}
    aria-hidden="true"
  >
    <circle cx="7.5" cy="15.5" r="4.5" />
    <path d="m10.7 12.3 9.8-9.8M17 6l3 3M15 8l2 2" />
  </svg>
);

const DownloadGlyph = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#fff"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

export type CredentialsDownloadDialogProps = {
  open: boolean;
  neverIssued: boolean;
  name: string;
  email: string;
  busy: boolean;
  onDownload: () => void;
  onClose: () => void;
};

export const getCredentialsDownloadMessage = ({
  neverIssued,
  name,
  email,
}: {
  neverIssued: boolean;
  name: string;
  email: string;
}) =>
  neverIssued
    ? `Login credentials for ${name} (${email}) will be generated. Download the credentials file and share it with the employee securely.`
    : `New login credentials for ${name} (${email}) will be generated and their current password will stop working. Download the credentials file and share it with the employee securely.`;

const CredentialsDownloadDialog = ({
  open,
  neverIssued,
  name,
  email,
  busy,
  onDownload,
  onClose,
}: CredentialsDownloadDialogProps) => (
  <DesignDialog
    open={open}
    onClose={onClose}
    busy={busy}
    variant="unsaved"
    iconNode={<KeyGlyph />}
    iconColor="#E1A731"
    panelStyle={{ maxWidth: 460 }}
    title={
      <span style={{ textTransform: "uppercase" }}>
        {neverIssued
          ? "Generate Employee Login Credentials"
          : "Re-generate Employee Login Credentials"}
      </span>
    }
    message={getCredentialsDownloadMessage({ neverIssued, name, email })}
    actions={
      <>
        <button
          type="button"
          disabled={busy}
          onClick={onClose}
          style={cancelButtonStyle}
          className="cursor-pointer hover:bg-[#F5F5F5] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cpaGold disabled:cursor-not-allowed disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onDownload}
          style={downloadButtonStyle}
          className="cursor-pointer bg-[#E1A731] hover:bg-[#C98F1F] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cpaGold disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy ? <CircularProgress color="inherit" size={16} /> : <DownloadGlyph />}
          Download
        </button>
      </>
    }
  />
);

export default CredentialsDownloadDialog;
